/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Inline } from '@atlaskit/primitives/compiled';
// eslint-disable-next-line import/order
import { useAISurfaceMetadata, useViewAIActions } from '@atlassian/ai-smart-button-api';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useProfileController } from '@atlassian/conversation-assistant-store';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIButton } from '../ai-button';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useDynamicSurfacePanel } from '../ai-dynamic-surface/useDynamicSurfacePanel';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIProactiveNudge } from '../ai-proactive-nudge';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { ObjectSidebar } from '../common/types/object-sidebar-types';

export type AISmartButtonProps = {
	objectSidebar: ObjectSidebar;
};

const styles = cssMap({
	buttonWrapper: {
		position: 'relative',
	},
});

export const AISmartButton = ({ objectSidebar }: AISmartButtonProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const dynamicSurfacePanel = useDynamicSurfacePanel(objectSidebar);
	const { setProfileData } = useViewAIActions();
	const surfaceMetadata = useAISurfaceMetadata();
	const profileData = useProfileController();

	useEffect(() => {
		if (profileData) {
			setProfileData(profileData);
		}
	}, [profileData, setProfileData]);

	const onAISmartButtonClick = React.useCallback(() => {
		const { isObjectSidebarShown, panel } = objectSidebar.state;
		const { hideObjectSidebar, showObjectSidebar } = objectSidebar.actions;
		if (isObjectSidebarShown && panel?.id === dynamicSurfacePanel.id) {
			hideObjectSidebar();
		} else {
			showObjectSidebar(dynamicSurfacePanel, 'push');
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: surfaceMetadata.contentType,
				action: 'clicked',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoSmartButton',
				attributes: {
					pageMode: surfaceMetadata.pageMode,
				},
			},
		}).fire();
	}, [dynamicSurfacePanel, objectSidebar, createAnalyticsEvent, surfaceMetadata]);

	return (
		<Inline space="space.100" alignBlock="center" xcss={styles.buttonWrapper}>
			<AIProactiveNudge />
			<AIButton onClick={onAISmartButtonClick} />
		</Inline>
	);
};
